// Sticky figure index (DESIGN_SPEC §10.6, §11.4.3; completed and partial documents only).
//   <FigIndex figs verdict state name />
//   opaque paper bar under the nav: "01 Behavior 8 · 02 ATT&CK 8 …" with scroll-spy (aria-current), the verdict banner
//   (dashed "· partial" unless the run completed) pinned at the right (first on phones) linking to #top. Detail drops
//   by width in components.css (numbers <1600 for the
//   banner's room, filename <1200, counts <1100); under 900px a "Sections · Behavior" button opens a menu (Esc closes, focus returns).
//   Keys: j / k next / previous figure, 1–9 jump.
import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { JobState, VerdictBanner } from "@/components/ui";
import { useHotkeys } from "@/hooks";
import { count } from "@/lib/format";
import { scrollBehavior } from "@/lib/motion";
import type { EffectiveState, VerdictSnap } from "@/lib/derive/types";
import type { PlannedFig } from "./figOrder";
import { scrollToId } from "./scroll";
import { stepFrom, useScrollSpy } from "./useScrollSpy";

const pad2 = (n: number) => String(n).padStart(2, "0");
const figHash = (no: number) => `fig-${no}`;

function scrollToFigure(no: number) {
  if (scrollToId(figHash(no))) history.replaceState(history.state, "", `#${figHash(no)}`);
}

export function FigIndex({
  figs,
  verdict,
  state,
  name,
}: {
  figs: readonly PlannedFig[];
  verdict: VerdictSnap | null;
  state: EffectiveState;
  name: string;
}) {
  // the figure the reader jumped to wins a tie with its neighbour in the same journal row
  const [picked, setPicked] = useState<string | null>(null);
  const current = useScrollSpy(
    figs.map((f) => figHash(f.no)),
    { prefer: picked },
  );
  const cur = figs.find((f) => figHash(f.no) === current) ?? figs[0];
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const tabs = useRef<HTMLOListElement>(null);
  const menuId = useId();

  const go = (no: number) => {
    setPicked(figHash(no));
    scrollToFigure(no);
  };
  const step = (d: 1 | -1) => {
    const id = stepFrom(
      figs.map((f) => figHash(f.no)),
      cur ? figHash(cur.no) : null,
      d,
    );
    const next = figs.find((f) => figHash(f.no) === id);
    if (next) go(next.no);
  };
  const jumps = Object.fromEntries(
    figs.slice(0, 9).map((f, i) => [String(i + 1), () => go(f.no)] as const),
  );
  useHotkeys({ j: () => step(1), k: () => step(-1), ...jumps }, { enabled: figs.length > 0 });

  // keep the current tab visible inside the (overflow safety-net) tab row
  useEffect(() => {
    const row = tabs.current;
    const el = row?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!row || !el || row.scrollWidth <= row.clientWidth) return;
    const l = el.offsetLeft - row.offsetLeft;
    if (l < row.scrollLeft || l + el.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollTo({ left: l - 24, behavior: "auto" });
  }, [current]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    menu.current?.querySelector<HTMLElement>('[aria-current="true"]')?.focus();
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onTab = (no: number) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    setOpen(false);
    go(no);
  };

  if (!figs.length) return null;
  return (
    <nav className="figindex" aria-label="Report sections">
      <div className="page figindex-in">
        <button
          ref={button}
          type="button"
          className="fi-sections"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => setOpen((o) => !o)}
        >
          {/* the narrowest phones keep the current figure's name; the prefix stays for screen readers */}
          <span className="max-[400px]:sr-only">Sections ·</span> <b className="font-medium">{cur?.label}</b>
        </button>
        {open && (
          <div ref={menu} id={menuId} className="fi-menu">
            {figs.map((f) => (
              <a key={f.no} href={`#${figHash(f.no)}`} aria-current={f.no === cur?.no ? "true" : undefined} onClick={onTab(f.no)}>
                <span>
                  <span className="mr-2.5 text-ink-faint">{pad2(f.no)}</span>
                  {f.label}
                </span>
                {f.count !== undefined && <span className="text-ink-faint tabular-nums">{count(f.count)}</span>}
              </a>
            ))}
          </div>
        )}
        <ol ref={tabs} className="fi-tabs">
          {figs.map((f) => (
            <li key={f.no}>
              <a className="fi-tab" href={`#${figHash(f.no)}`} aria-current={f.no === cur?.no ? "true" : undefined} onClick={onTab(f.no)}>
                <span className="fi-no">{pad2(f.no)}</span>
                {f.label}
                {f.count !== undefined && <span className="ct">{count(f.count)}</span>}
              </a>
            </li>
          ))}
        </ol>
        <a
          className="fi-verdict"
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: scrollBehavior() });
          }}
        >
          {verdict ? <VerdictBanner v={verdict.label} score={verdict.score} size="sm" partial={state !== "completed"} /> : <JobState s={state} />}
          <span className="fi-name" translate="no">
            {name}
          </span>
        </a>
      </div>
    </nav>
  );
}
